/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/ScopeAgGridSection.tsx
 * FILE_ID: QB-AGGRID-SECTION-001
 * PURPOSE: AG-Grid Excel-grade editor for a single quote section.
 * KEYBOARD: Enter/Tab/Arrows/F2 (native), Ctrl+D (fill-down), Ctrl+V (paste TSV).
 * SIZE WAIVER (>300 lines): this is the section-level orchestrator —
 *      every persist hook (cell edit, paste, fill-down, pad promote,
 *      row delete/duplicate, formula bar, context menu, pricelist
 *      picker, application picker) is colocated here so the chrome
 *      + grid + modals share a single rows[] source of truth. Each
 *      sub-hook already lives in its own file under the 300-line cap.
 * RELATED: QB-AGGRID-LAZY-001 (wrapper), QB-AGGRID-COLUMNS-001 (schema),
 *      QB-AGGRID-APPLICATION-PICKER-001 (use-scope-ag-application-picker)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { QuoteSection, QuoteLineItem } from '@/lib/quoteApi'
import type { PriceBookItem } from '@/lib/api/pricing-api/types'
import type { CustomColumnDef } from '../scope-sheet-grid/ScopeSheetGridTypes'
import {
  buildAgGridColumns,
  flattenEditableFieldIds,
} from './scope-ag-grid-columns'
import LazyAgGrid, { type ScopeGridFocusRequest } from './lazy-aggrid-impl'
import ScopeAgFormulaBar from './ScopeAgFormulaBar'
import ScopeAgGridChrome from './ScopeAgGridChrome'
import type { ScopeGridDensity } from './ScopeSectionToolbar'
import ScopeGridFooterHints from './ScopeGridFooterHints'
import ScopeAgGridContextMenu from './ScopeAgGridContextMenu'
import { useTrailingEmptyRows } from './useTrailingEmptyRows'
import { useScopeAgPersistHandlers } from './useScopeAgPersistHandlers'
import { useScopeAgRowActions } from './useScopeAgRowActions'
import { useScopeAgContextMenu } from './useScopeAgContextMenu'
import { useScopeAgFormulaBar } from './useScopeAgFormulaBar'
import { buildLumpSumPayload } from './lump-sum-helpers'
import { type PrefillTarget, usePricelistPicker } from './usePricelistPicker'
import ScopePricelistDrawer from './ScopePricelistDrawer'
import { mapPickedItemToPriceBookItem } from './mapPickedItemToPriceBookItem'
import ScopePricelistFastAdd, {
  type ScopePricelistFastAddHandle,
} from './ScopePricelistFastAdd'
import { buildPricelistCreatePayload } from './pricelist-prefill'
import { useScopeAgApplicationPicker } from './use-scope-ag-application-picker'
import ApplicationSelectorModal from '../application-selector-modal/ApplicationSelectorModal'
import { useQuoteBuilderTradeMode } from '@/hooks/useQuoteBuilderTradeMode'

// Columns that ship on the client-facing PDF today. Hardcoded for now
// — there's no quote.config.client_pdf_columns yet, but every quote
// PDF template uses this same set, so the banner stays honest.
const CLIENT_VISIBLE_LABELS = ['Location', 'Description', 'Qty', 'Unit', 'Total']

interface Props {
  section: QuoteSection
  isEditable: boolean
  visibleOptionalIds: string[]
  customColumns: CustomColumnDef[]
  configDefaults: {
    defaultProductivityPmpd?: number | string | null
    defaultUnit?: string
  }
  // Widened in QB-AGGRID-PHANTOM-001 to mirror the legacy PhantomRow
  // contract — estimators may seed any chargeout field on creation.
  onAddItem: (
    sectionId: string,
    data: {
      location?: string
      description: string
      quantity: number
      unit: string
      productivity_pmpd: number
      labour_chargeout?: number | null
      material_chargeout?: number | null
      plant_chargeout?: number | null
      subcon_chargeout?: number | null
    },
  ) => void
  onUpdateItem: (
    sectionId: string,
    itemId: string,
    patch: Partial<QuoteLineItem>,
  ) => void
  onDeleteItem: (sectionId: string, itemId: string) => void
  // Fires when "+ Add section" is clicked in the toolbar; parent
  // (ScopeAgGridEditor) owns section creation.
  onAddSection?: () => void
  onPasteWithCreate?: (
    sectionId: string,
    existingItems: QuoteLineItem[],
    pasteData: Array<Record<string, unknown>>,
    startRow: number,
  ) => Promise<void>
}

export default function ScopeAgGridSection({
  section,
  isEditable,
  visibleOptionalIds,
  customColumns,
  configDefaults,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  onAddSection,
  onPasteWithCreate,
}: Props) {
  // SAM-style toolbar state — kept local to each section because
  // density / client-labels / reset are per-grid affordances, not
  // workspace-wide preferences.
  const [density, setDensity] = useState<ScopeGridDensity>('comfortable')
  const [clientLabelsOn, setClientLabelsOn] = useState(false)
  const [resetSignal, setResetSignal] = useState(0)
  // Split-screen pricelist drawer (opt-in; closed = today's full-width layout).
  const [pricelistDrawerOpen, setPricelistDrawerOpen] = useState(false)
  const fastAddRef = useRef<ScopePricelistFastAddHandle | null>(null)
  const [gridFocusRequest, setGridFocusRequest] =
    useState<ScopeGridFocusRequest | null>(null)
  const awaitingFastAddQuantityRef = useRef(false)
  // Backend exposes `section_subtotal` on the serializer (matching the
  // QuoteSection model field). We previously read `subtotal` here,
  // which was undefined → toolbar always rendered "N lines · $0.00"
  // even when the underlying line items summed to thousands of dollars.
  // Fall back to either name so this stays robust if a future API
  // version renames the field again.
  const sectionWithTotals = section as unknown as {
    section_subtotal?: number | string | null
    subtotal?: number | string | null
  }
  const sectionSubtotal =
    sectionWithTotals.section_subtotal ?? sectionWithTotals.subtotal
  // Trade mode (waterproofing / steel / custom) drives whether the
  // steel-fabrication allowance bucket band is rendered. We pass
  // `effectiveTradeMode` (which falls back to 'waterproofing' during
  // the initial fetch) so first paint never flashes the steel band
  // for waterproofing tenants — the dominant case for Bravada.
  const { effectiveTradeMode } = useQuoteBuilderTradeMode()
  const columnDefs = useMemo(
    () =>
      buildAgGridColumns({
        visibleOptionalIds,
        customColumns,
        isEditable,
        tradeMode: effectiveTradeMode,
      }),
    [visibleOptionalIds, customColumns, isEditable, effectiveTradeMode],
  )

  const editableFieldIds = useMemo(
    () => flattenEditableFieldIds(columnDefs),
    [columnDefs],
  )

  const rows = useMemo(
    () =>
      (section.items ?? [])
        .slice()
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [section.items],
  )

  // SAM-style trailing pads: AG-Grid renders real rows + N amber-tinted
  // blanks (styled in lazy-aggrid-impl). Pads promote into real rows on
  // first cell commit via onAddItem.
  const { displayRows, padSeed } = useTrailingEmptyRows({
    realRows: rows,
    sectionId: section.id,
    configDefaults,
    isEditable,
  })

  const focusQuantityForPrefillTarget = useCallback(
    (target: PrefillTarget) => {
      setGridFocusRequest({
        nonce: Date.now(),
        field: 'quantity',
        rowId: target.kind === 'update' ? target.rowId : undefined,
        minRealRowCount: target.kind === 'create' ? rows.length + 1 : undefined,
        startEditing: true,
      })
    },
    [rows.length],
  )

  const handleFastAddPick = useCallback(
    (item: PriceBookItem) => {
      if (!isEditable) return
      const add = onAddItem as (s: string, d: Record<string, unknown>) => void
      add(section.id, buildPricelistCreatePayload(item, padSeed))
      awaitingFastAddQuantityRef.current = true
      focusQuantityForPrefillTarget({ kind: 'create' })
    },
    [focusQuantityForPrefillTarget, isEditable, onAddItem, padSeed, section.id],
  )

  const handleQuantityCommitted = useCallback(() => {
    if (!awaitingFastAddQuantityRef.current) return
    awaitingFastAddQuantityRef.current = false
    window.requestAnimationFrame(() => fastAddRef.current?.focus())
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && pricelistDrawerOpen) {
        event.preventDefault()
        setPricelistDrawerOpen(false)
        return
      }
      if (event.altKey && event.key.toLowerCase() === 'p') {
        event.preventDefault()
        if (isEditable) setPricelistDrawerOpen(true)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isEditable, pricelistDrawerOpen])

  // All persist callbacks (cell edits, pad promotion, paste, fill-down)
  // live in useScopeAgPersistHandlers — keeps this component as pure
  // orchestration (layout + grid wiring + formula bar) under the 300-line cap.
  const { handleCellEdit, handlePadPromote, handleFillDown, handlePasteRange } =
    useScopeAgPersistHandlers({
      sectionId: section.id,
      rows,
      displayRows,
      editableFieldIds,
      isEditable,
      padSeed,
      onAddItem: onAddItem as (
        sectionId: string,
        data: Record<string, unknown>,
      ) => void,
      onUpdateItem,
      onPasteWithCreate,
    })

  // Formula bar state + focus tracking (drives pricelist picker target too).
  const {
    formulaCell,
    handleFocusChange,
    commitFormulaEdit,
    handleDeleteFocused,
    focusedRow,
  } = useScopeAgFormulaBar({
    rows,
    isEditable,
    onCellEdit: handleCellEdit,
    onDeleteItem,
    sectionId: section.id,
  })

  // SAM-style per-row Copy + Delete callbacks (rendered by the
  // right-hand actions column). Both routes share the same persist
  // contract as the formula-bar Delete key so behaviour stays uniform.
  const { handleDeleteRow, handleDuplicateRow } = useScopeAgRowActions({
    sectionId: section.id,
    rowCount: rows.length,
    isEditable,
    onAddItem: onAddItem as (s: string, d: Record<string, unknown>) => void,
    onDeleteItem,
  })

  // SAM-style "+ Lump sum": seeds a row in manual pricing mode so
  // row total = override_chargeout (no q*rate calc).
  const handleAddLumpSum = useCallback(() => {
    if (!isEditable) return
    const add = onAddItem as (s: string, d: Record<string, unknown>) => void
    add(section.id, buildLumpSumPayload(rows.length + 1))
  }, [isEditable, onAddItem, rows.length, section.id])

  // Right-click context menu (fixed-position popup outside the grid).
  const ctxMenu = useScopeAgContextMenu({
    sectionId: section.id,
    rows,
    isEditable,
    configDefaults,
    onAddItem: onAddItem as (s: string, d: Record<string, unknown>) => void,
    onDeleteItem,
  })

  // SAM-style pricelist picker: a modal that searches pricebook items
  // and prefills description / unit / adjusted_rate on the focused
  // (or topmost pad) row when the user picks one.
  const pricelist = usePricelistPicker({
    sectionId: section.id,
    rows,
    padSeed,
    isEditable,
    focusedRowId: formulaCell.itemId,
    onAddItem: onAddItem as (s: string, d: Record<string, unknown>) => void,
    onUpdateItem,
    onPrefillTarget: focusQuantityForPrefillTarget,
  })

  // Bravada pricelist takeoff: row-actions "Apply application" button
  // opens this picker. On select it PATCHes application_id; backend
  // Application.apply_application autofills product slot materials,
  // product_system, and productivity_pmpd. The autofill lands in the
  // sub-band columns added in MR3 once React Query refetches.
  // Only exposed for waterproofing; steel/SAM don't yet have a
  // matching application catalogue so the row-action stays hidden.
  const applicationPicker = useScopeAgApplicationPicker({
    sectionId: section.id,
    onUpdateItem,
  })
  const supportsApplications =
    isEditable && effectiveTradeMode === 'waterproofing'

  return (
    <div className="flex h-full min-h-0">
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col gap-2">
      <ScopeAgGridChrome
        sectionId={section.id}
        sectionTitle={section.title}
        itemCount={rows.length}
        sectionSubtotal={sectionSubtotal}
        density={density}
        onDensityChange={setDensity}
        isEditable={isEditable}
        onResetWidths={() => setResetSignal((n) => n + 1)}
        onAddRow={() => handlePadPromote('__pad__0', 'description', '')}
        onAddSection={() => onAddSection?.()}
        onAddLumpSum={isEditable ? handleAddLumpSum : undefined}
        onOpenPricelist={isEditable ? () => setPricelistDrawerOpen(true) : undefined}
        onToggleClientLabels={() => setClientLabelsOn((v) => !v)}
        isClientLabelsOn={clientLabelsOn}
        clientVisibleLabels={CLIENT_VISIBLE_LABELS}
      />

      {isEditable && (
        <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-900/40">
          <span className="whitespace-nowrap font-medium text-slate-600 dark:text-slate-300">
            Add product
          </span>
          <ScopePricelistFastAdd
            ref={fastAddRef}
            isEditable={isEditable}
            onPickItem={handleFastAddPick}
            onOpenDrawer={() => setPricelistDrawerOpen(true)}
          />
          <span className="hidden whitespace-nowrap text-[10px] text-slate-500 dark:text-slate-400 md:inline">
            Type code/name, Enter, Qty, Enter
          </span>
        </div>
      )}

      <ScopeAgFormulaBar
        focusedRow={focusedRow ?? null}
        field={formulaCell.field}
        value={formulaCell.value}
        rowIndex={focusedRow ? rows.indexOf(focusedRow) : null}
        isEditable={isEditable}
        onChange={commitFormulaEdit}
        onDeleteFocused={handleDeleteFocused}
      />

      {/* AG-Grid (SAM-style: realRows + N trailing pad rows). The pads
          are amber-tinted blank rows; the user types into them and they
          promote into real rows on first cell commit via onPromotePadRow. */}
      <div className="flex-1 min-h-[400px]">
        <LazyAgGrid
          rows={displayRows}
          columnDefs={columnDefs}
          editableFieldIds={editableFieldIds}
          isEditable={isEditable}
          density={density}
          resetSignal={resetSignal}
          onCellEdit={handleCellEdit}
          onPasteRange={handlePasteRange}
          onFillDown={handleFillDown}
          onFocusChange={handleFocusChange}
          onPromotePadRow={handlePadPromote}
          onDeleteRow={handleDeleteRow}
          onDuplicateRow={handleDuplicateRow}
          onApplyApplication={
            supportsApplications ? applicationPicker.openForRow : undefined
          }
          onRowContextMenu={ctxMenu.openAt}
          focusRequest={gridFocusRequest}
          onQuantityCommitted={handleQuantityCommitted}
        />
      </div>

      {isEditable && <ScopeGridFooterHints />}

      {/* Right-click menu — fixed-position popup overlays the grid. */}
      <ScopeAgGridContextMenu
        open={ctxMenu.state.open}
        x={ctxMenu.state.x}
        y={ctxMenu.state.y}
        onClose={ctxMenu.close}
        onInsertAbove={ctxMenu.handleInsertAbove}
        onInsertBelow={ctxMenu.handleInsertBelow}
        onDuplicate={ctxMenu.handleDuplicate}
        onDelete={ctxMenu.handleDelete}
      />

      {/* Bravada application picker: when an estimator clicks the
          row-actions "Apply application" icon, this modal opens with
          the row's description seeded as the search query. On select
          we PATCH application_id; backend autofills the product slot
          materials, product_system, and productivity_pmpd. */}
      {supportsApplications && (
        <ApplicationSelectorModal
          isOpen={applicationPicker.state.isOpen}
          onClose={applicationPicker.close}
          onSelect={applicationPicker.handleSelect}
          context={applicationPicker.state.context}
          selectedId={applicationPicker.state.selectedId}
        />
      )}
      </div>

      {/* Split-screen pricelist drawer — replaces the old modal overlay.
          Docked beside the grid (resizable), renders the full editable
          workbook; each row's "→" prefills the focused/pad quote line via
          the same usePricelistPicker.handleSelect path the modal used. */}
      {pricelistDrawerOpen && (
        <ScopePricelistDrawer
          onClose={() => setPricelistDrawerOpen(false)}
          onPickRow={(picked) => pricelist.handleSelect(mapPickedItemToPriceBookItem(picked))}
        />
      )}
    </div>
  )
}
